import { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'

import { cn } from '@/app/utils/cn'
import { Badge } from '@/view/components/ui/badge'
import {
  SUPPORT_REASON_LABEL,
  SUPPORT_STATUS_BADGE_CLASSNAME,
  SUPPORT_STATUS_LABEL,
  SupportRequest,
} from '@/app/entities/SupportRequest'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

const columnHelper = createColumnHelper<SupportRequest>()

const getSellerName = ({ seller, metadata }: SupportRequest) => {
  if (typeof seller === 'object' && seller?.name) {
    return seller?.name || '-'
  }

  return metadata?.contact?.name || '-'
}

export const SupportRequestColumnsDefinition = (isStaffView?: boolean) => {
  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor('number', {
        id: 'number',
        header: () => <div className="pl-4 text-left">Número</div>,
        cell: ({ row }) => (
          <div className="flex items-center gap-2 pl-4 text-left font-semibold text-black01">
            #{row.original.number || '-'}
            {row.original.hasUnreadMessage && (
              <span
                aria-label="Nova mensagem"
                className="size-2 shrink-0 rounded-full bg-primary"
              />
            )}
          </div>
        ),
      }),
      columnHelper.accessor('reason', {
        id: 'reason',
        header: () => <div className="text-left">Motivo</div>,
        cell: ({ row }) => (
          <div className="text-left">
            {SUPPORT_REASON_LABEL[row.original.reason] || '-'}
          </div>
        ),
      }),
    ]

    if (isStaffView) {
      columns.push(
        columnHelper.accessor('seller', {
          id: 'seller',
          header: () => <div className="text-left">Vendedor</div>,
          cell: ({ row }) => (
            <div className="text-left">{getSellerName(row.original)}</div>
          ),
        }) as (typeof columns)[number],
      )
    }

    columns.push(
      columnHelper.accessor('status', {
        id: 'status',
        header: () => <div className="text-center">Status</div>,
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Badge
              className={cn(
                'border-transparent font-semibold',
                SUPPORT_STATUS_BADGE_CLASSNAME[row.original.status],
              )}
            >
              {SUPPORT_STATUS_LABEL[row.original.status]}
            </Badge>
          </div>
        ),
      }) as (typeof columns)[number],
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: () => <div className="text-center">Aberto em</div>,
        cell: ({ row }) => (
          <div className="text-center">
            {format(new Date(row.original.createdAt), 'dd/MM/yyyy, HH:mm', {
              locale: ptBR,
            })}
          </div>
        ),
      }) as (typeof columns)[number],
      columnHelper.accessor('updatedAt', {
        id: 'updatedAt',
        header: () => <div className="pr-4 text-center">Atualizado em</div>,
        cell: ({ row }) => (
          <div className="pr-4 text-center">
            {format(new Date(row.original.updatedAt), 'dd/MM/yyyy, HH:mm', {
              locale: ptBR,
            })}
          </div>
        ),
      }) as (typeof columns)[number],
    )

    return columns
  }, [isStaffView])

  return { columns }
}
